import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Modal from "@mui/material/Modal";
import * as AccessTimeIconModule from "@mui/icons-material/AccessTime.js";
import * as ChevronLeftIconModule from "@mui/icons-material/ChevronLeft.js";
import * as ChevronRightIconModule from "@mui/icons-material/ChevronRight.js";
import * as CloseIconModule from "@mui/icons-material/Close.js";
import * as HistoryIconModule from "@mui/icons-material/History.js";
import * as RestoreIconModule from "@mui/icons-material/Restore.js";

const AccessTimeIcon = AccessTimeIconModule.default;
const ChevronLeftIcon = ChevronLeftIconModule.default;
const ChevronRightIcon = ChevronRightIconModule.default;
const CloseIcon = CloseIconModule.default;
const HistoryIcon = HistoryIconModule.default;
const RestoreIcon = RestoreIconModule.default;

const HISTORY_MODAL_STYLE = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "min(980px, calc(100vw - 24px))",
  maxHeight: "calc(100vh - 32px)",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 0,
  borderRadius: 2,
  overflow: "hidden",
};

function formatDateTime(value) {
  if (!value) {
    return "Unknown time";
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return "Unknown time";
  }

  return parsed.toLocaleString();
}

function getActorLabel(summary) {
  if (!summary?.updated_by) {
    return summary?.actor_type || "Unknown";
  }

  return `${summary.updated_by} (${summary.actor_type || "Unknown"})`;
}

function getChangeColor(changeType) {
  switch (changeType) {
    case "CREATED":
      return "success";
    case "DELETED":
      return "error";
    case "UPDATED":
    default:
      return "info";
  }
}

function getDefaultSummaryTitle(summary, resourceLabel) {
  return `${resourceLabel} v${summary?.version_no || "-"}`;
}

export function HistoryActionButton({ disabled = false, onClick }) {
  return (
    <Button
      disabled={disabled}
      onClick={onClick}
      startIcon={<HistoryIcon />}
      sx={{
        backgroundColor: "#ffffff",
        border: "1px solid #cbd5e1",
        borderRadius: "999px",
        color: "#243245",
        fontSize: "14px",
        fontWeight: 700,
        minWidth: "auto",
        px: 2,
        py: 1,
        textTransform: "none",
        "&:hover": {
          backgroundColor: "#f8fafc",
          borderColor: "#94a3b8",
        },
      }}
      variant="contained"
    >
      History
    </Button>
  );
}

export function HistoryViewingBanner({
  onOpenHistory,
  onRestore,
  onShowCurrent,
  onViewNewer,
  onViewOlder,
  restoreDisabled = false,
  restoring = false,
  summary,
}) {
  if (!summary || summary.is_current) {
    return null;
  }

  return (
    <div className="mb-5 rounded-[8px] border border-amber-200 bg-[linear-gradient(135deg,#fff8e6_0%,#ffffff_100%)] px-5 py-4 shadow-[0_10px_28px_rgba(148,116,20,0.08)]">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-amber-100 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-amber-800">
              Viewing version {summary.version_no}
            </span>
            <span className="text-sm font-semibold text-slate-600">
              Saved {formatDateTime(summary.created_at)}
            </span>
          </div>
          <p className="mt-2 text-sm text-slate-700">
            This is a read-only historical snapshot from {getActorLabel(summary)}.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <IconButton
            aria-label="View newer version"
            disabled={!onViewNewer}
            onClick={onViewNewer}
            size="small"
            sx={{ border: "1px solid #e2e8f0" }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <IconButton
            aria-label="View older version"
            disabled={!onViewOlder}
            onClick={onViewOlder}
            size="small"
            sx={{ border: "1px solid #e2e8f0" }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
          <Button
            onClick={onShowCurrent}
            sx={{ textTransform: "none" }}
            variant="outlined"
          >
            Return to current
          </Button>
          <Button
            onClick={onOpenHistory}
            startIcon={<HistoryIcon />}
            sx={{ textTransform: "none" }}
            variant="outlined"
          >
            View timeline
          </Button>
          <Button
            disabled={restoreDisabled}
            onClick={onRestore}
            startIcon={<RestoreIcon />}
            sx={{ textTransform: "none" }}
            variant="contained"
          >
            {restoring ? "Restoring..." : "Restore this version"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function VersionHistoryControl({
  activeSummary,
  canViewNewer = false,
  canViewOlder = false,
  detailState = "idle",
  dialogOpen = false,
  disabled = false,
  errorMessage = "",
  onClose,
  onOpen,
  onRestore,
  onSelectVersion,
  onShowCurrent,
  onViewNewer,
  onViewOlder,
  resourceLabel,
  restoreState = "idle",
  summaries = [],
  summaryState = "idle",
  summaryTitle = null,
}) {
  const resolvedSummaryTitle =
    summaryTitle ||
    ((summary) => getDefaultSummaryTitle(summary, resourceLabel || "Version"));

  return (
    <>
      <HistoryActionButton disabled={disabled} onClick={onOpen} />
      <Modal onClose={onClose} open={dialogOpen}>
        <Box sx={HISTORY_MODAL_STYLE}>
          <div className="flex items-center justify-between border-b border-slate-200 bg-[linear-gradient(135deg,#eff6ff_0%,#ffffff_100%)] px-6 py-5">
            <div>
              <p className="text-[12px] font-bold uppercase tracking-[0.18em] text-slate-400">
                Version History
              </p>
              <h2 className="mt-1 text-[28px] font-black text-slate-900">
                {resourceLabel}
              </h2>
              <p className="mt-2 text-sm text-slate-600">
                Choose a saved version to preview its values and restore it if needed.
              </p>
            </div>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </div>

          <div className="grid max-h-[calc(100vh-170px)] min-h-[460px] grid-cols-1 overflow-hidden lg:grid-cols-[340px_minmax(0,1fr)]">
            <div className="border-r border-slate-200 bg-slate-50">
              <div className="border-b border-slate-200 px-4 py-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-700">Saved Versions</p>
                  <div className="flex items-center gap-1">
                    <IconButton
                      aria-label="View newer version"
                      disabled={!canViewNewer}
                      onClick={onViewNewer}
                      size="small"
                    >
                      <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      aria-label="View older version"
                      disabled={!canViewOlder}
                      onClick={onViewOlder}
                      size="small"
                    >
                      <ChevronRightIcon fontSize="small" />
                    </IconButton>
                  </div>
                </div>
              </div>
              <div className="max-h-[calc(100vh-250px)] overflow-auto p-3">
                {summaryState === "loading" ? (
                  <p className="px-2 py-3 text-sm text-slate-600">Loading history...</p>
                ) : summaries.length === 0 ? (
                  <p className="px-2 py-3 text-sm text-slate-600">
                    No saved versions were returned for this record.
                  </p>
                ) : (
                  summaries.map((summary) => {
                    const isActive =
                      String(activeSummary?.version_no) === String(summary.version_no);

                    return (
                      <button
                        className={`mb-2 w-full rounded-[8px] border px-4 py-4 text-left transition ${
                          isActive
                            ? "border-[#243245] bg-white shadow-[0_10px_24px_rgba(36,50,69,0.08)]"
                            : "border-transparent bg-transparent hover:border-slate-200 hover:bg-white"
                        }`}
                        key={summary.version_no}
                        onClick={() => onSelectVersion(summary.version_no)}
                        type="button"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-bold text-slate-900">
                              {resolvedSummaryTitle(summary)}
                            </p>
                            <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                              <AccessTimeIcon sx={{ fontSize: 14 }} />
                              <span>{formatDateTime(summary.created_at)}</span>
                            </div>
                          </div>
                          {summary.is_current ? (
                            <span className="rounded-full bg-emerald-100 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-emerald-700">
                              Current
                            </span>
                          ) : null}
                        </div>
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <Chip
                            color={getChangeColor(summary.change_type)}
                            label={summary.change_type || "UPDATED"}
                            size="small"
                            variant="outlined"
                          />
                          <span className="text-xs text-slate-500">
                            {summary.updated_by || summary.actor_type || "Unknown actor"}
                          </span>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            <div className="flex flex-col overflow-auto bg-white">
              <div className="px-6 py-5">
                {activeSummary ? (
                  <>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-700">
                        Version {activeSummary.version_no}
                      </span>
                      {activeSummary.is_current ? (
                        <span className="rounded-full bg-emerald-100 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-700">
                          Live now
                        </span>
                      ) : null}
                    </div>
                    <h3 className="mt-4 text-[24px] font-black text-slate-900">
                      {resolvedSummaryTitle(activeSummary)}
                    </h3>
                    <div className="mt-5 grid gap-4 md:grid-cols-2">
                      <div className="rounded-[8px] border border-slate-200 bg-slate-50 px-4 py-4">
                        <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">
                          Saved On
                        </p>
                        <p className="mt-2 text-sm font-semibold text-slate-800">
                          {formatDateTime(activeSummary.created_at)}
                        </p>
                      </div>
                      <div className="rounded-[8px] border border-slate-200 bg-slate-50 px-4 py-4">
                        <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">
                          Saved By
                        </p>
                        <p className="mt-2 text-sm font-semibold text-slate-800">
                          {getActorLabel(activeSummary)}
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 rounded-[8px] border border-slate-200 bg-white px-4 py-4">
                      <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">
                        Change Comment
                      </p>
                      <p className="mt-2 text-sm leading-6 text-slate-700">
                        {activeSummary.change_comment || "No comment was saved for this version."}
                      </p>
                    </div>
                    <Divider sx={{ my: 3 }} />
                    <div className="flex flex-wrap items-center gap-3">
                      <Button
                        onClick={onShowCurrent}
                        sx={{ textTransform: "none" }}
                        variant="outlined"
                      >
                        Jump to current
                      </Button>
                      {!activeSummary.is_current ? (
                        <Button
                          disabled={restoreState === "saving"}
                          onClick={onRestore}
                          startIcon={<RestoreIcon />}
                          sx={{ textTransform: "none" }}
                          variant="contained"
                        >
                          {restoreState === "saving"
                            ? "Restoring..."
                            : "Restore this version"}
                        </Button>
                      ) : null}
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-slate-600">Select a version to inspect it.</p>
                )}

                {detailState === "loading" ? (
                  <p className="mt-6 text-sm text-slate-600">
                    Loading the selected version into the page...
                  </p>
                ) : null}
                {errorMessage ? (
                  <p className="mt-6 text-sm text-[#c62828]">{errorMessage}</p>
                ) : null}
              </div>
            </div>
          </div>
        </Box>
      </Modal>
    </>
  );
}
